export type WordDocument = {
    id: string;
    fileName: string;
    s3Url: string;
    createdAt: string;
    folderId?: string | null;
  };
  
  export type UploadProgress = {
    fileName: string;
    progress: number;
    error?: string;
  };
  
  export async function fetchWordDocuments(tenantId: string, page = 0, size = 10, search = "", sortBy = "createdAt", sortOrder = "desc", automationName?: string) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      search,
      sortBy,
      sortOrder
    });
    if (automationName) {
      queryParams.append('automationName', automationName);
    }
    const response = await fetch(`/api/tenants/${tenantId}/words?${queryParams.toString()}`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error("Failed to fetch documents");
    }
    return response.json();
  }

  export async function uploadFile(
    tenantId: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void,
    folderId?: string | null
  ): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    if (folderId) {
      formData.append("folderId", folderId);
    }
  
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded * 100) / event.total);
          onProgress({
            fileName: file.name,
            progress
          });
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          const error = new Error("Failed to upload file");
          if (onProgress) {
            onProgress({
              fileName: file.name,
              progress: 0,
              error: "Upload failed"
            });
          }
          reject(error);
        }
      });

      xhr.addEventListener("error", () => {
        const error = new Error("Network error during upload");
        if (onProgress) {
          onProgress({
            fileName: file.name,
            progress: 0,
            error: "Network error"
          });
        }
        reject(error);
      });

      xhr.open("POST", `/api/tenants/${tenantId}/words/upload`);
      xhr.send(formData);
    });
  }

  export async function uploadMultipleFiles(
    tenantId: string,
    files: File[],
    onProgress?: (progress: UploadProgress) => void,
    folderId?: string | null
  ): Promise<void> {
    const uploadPromises = files.map(file => uploadFile(tenantId, file, onProgress, folderId));
    await Promise.all(uploadPromises);
  }

  /** Usuwa dokument na podstawie ID */
export async function deleteWordDocument(tenantId: string, documentId: string) {
    const response = await fetch(`/api/tenants/${tenantId}/words/${documentId}`, {
    method: "DELETE",
    credentials: 'include'
  });

  if (!response.ok) throw new Error("Błąd usuwania dokumentu");
}

/** Pobiera plik na podstawie ID */
export async function downloadWordDocument(tenantId: string, documentId: string) {
    const response = await fetch(`/api/tenants/${tenantId}/words/download/${documentId}`, { credentials: 'include' });

    if (!response.ok) throw new Error("Błąd pobierania dokumentu");

    // Pobierz nazwę pliku z nagłówka Content-Disposition
    const disposition = response.headers.get("Content-Disposition");
    let fileName = `document-${documentId}.docx`;
    if (disposition) {
      const match = disposition.match(/filename=\"?([^\";]+)\"?/);
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1]);
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

// --- FOLDERY SZABLONÓW ---
export interface TemplateFolder {
  id: string;
  name: string;
  createdAt?: string;
  parentId?: string | null;
  documents?: any[];
  children?: any[];
}

export async function fetchTemplateFolders(tenantId: string, parentId?: string) {
  const url = new URL(`/api/tenants/${tenantId}/template-folders`, window.location.origin);
  if (parentId !== undefined) {
    url.searchParams.set('parentId', parentId);
  }
  const response = await fetch(url.toString(), { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd ładowania folderów szablonów');
  return response.json();
}

export async function addTemplateFolder(tenantId: string, name: string, parentId?: string | null) {
  const body: any = { name };
  if (typeof parentId === 'string' && parentId.length > 0) {
    body.parentId = parentId;
  }
  const response = await fetch(`/api/tenants/${tenantId}/template-folders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include'
  });
  if (response.status === 409) {
    throw new Error('Folder o tej nazwie już istnieje w tej lokalizacji.');
  }
  if (!response.ok) {
    let msg = 'Błąd dodawania folderu szablonów';
    try {
      const data = await response.json();
      if (data && data.error) throw new Error(data.error);
    } catch {}
    throw new Error(msg);
  }
  return response.json();
}

export async function deleteTemplateFolder(tenantId: string, folderId: string) {
  const response = await fetch(`/api/tenants/${tenantId}/template-folders/${folderId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Błąd usuwania folderu szablonów');
  return;
}

export async function fetchTemplateFolderById(tenantId: string, folderId: string): Promise<TemplateFolder> {
  const response = await fetch(`/api/tenants/${tenantId}/template-folders/${folderId}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd ładowania folderu szablonów');
  return response.json();
}

export async function updateTemplateFolderName(tenantId: string, folderId: string, newName: string) {
  const response = await fetch(`/api/tenants/${tenantId}/template-folders/${folderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: newName }),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Błąd edycji nazwy folderu szablonów');
  return response.json();
}

export async function updateWordDocumentFolder(tenantId: string, documentId: string, folderId: string | null) {
  const formData = new FormData();
  if (folderId) {
    formData.append('folderId', folderId);
  }
  const response = await fetch(`/api/tenants/${tenantId}/words/${documentId}/update-folder`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Błąd przenoszenia szablonu do folderu');
  return response.text();
}

export type WordDocumentTreeDto = {
  id: string;
  fileName: string;
  s3Url: string;
  createdAt: string;
};

export type FolderTreeDto = {
  id: string;
  name: string;
  tenantId: string;
  createdAt: string;
  parentId: string | null;
  children: FolderTreeDto[];
  documents: WordDocumentTreeDto[];
};

export async function fetchFolderTree(tenantId: string): Promise<FolderTreeDto[]> {
  const response = await fetch(`/api/tenants/${tenantId}/templates-tree`, { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd ładowania drzewa folderów');
  return response.json();
}

export type WordDocumentSearchResult = {
  id: string;
  fileName: string;
  folderId: string | null;
  s3Url?: string;
  createdAt?: string;
  automationName?: string;
  automationId?: string;
};

export async function searchWordDocuments(tenantId: string, query: string): Promise<WordDocumentSearchResult[]> {
  const response = await fetch(`/api/tenants/${tenantId}/templates-search?query=${encodeURIComponent(query)}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd wyszukiwania szablonów');
  return response.json();
}

export async function updateWordDocumentName(tenantId: string, documentId: string, newName: string) {
  const response = await fetch(`/api/tenants/${tenantId}/words/${documentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: newName }),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Błąd edycji nazwy szablonu');
  return response.json();
}